import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Link from '@mui/material/Link'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import { useMe } from '@shared/providers/src/MeProvider'
import { PatientCBONotAllowedReason } from '@shared/utils'

import useFormatMessage from '@hooks/useFormatMessage'
import useSupportChat from '@hooks/useSupportChat'
import { usePatientUpdate } from '@pages/Account/Account.hooks'
import { styles as dialogStyles } from '@components/Dialog/Dialog.utils'

const notAllowedDescription = {
  [PatientCBONotAllowedReason.Uninsured]: 'enrollment.cboStep.assignmentUpdate.uninsured',
  [PatientCBONotAllowedReason.OutsideCoverageInsured]: 'enrollment.cboStep.assignmentUpdate.outsideCoverageInsured',
  [PatientCBONotAllowedReason.OutsideCoverageUninsured]: 'enrollment.cboStep.assignmentUpdate.outsideCoverageUninsured',
}

const insuranceLinks = {
  prideHealth: (
    <Link href="https://pridehealth.us/" target="_blank" rel="noopener noreferrer">
      Pride Health
    </Link>
  ),
  americanExchange: (
    <Link href="https://americanexchange.com/" target="_blank" rel="noopener noreferrer">
      American Exchange
    </Link>
  ),
}

export function SubscribeDialog({ reason }) {
  const testId = 'subscribe-dialog'
  const formatMessage = useFormatMessage()
  const contactSupport = useSupportChat()

  const patientUpdate = usePatientUpdate()

  return (
    <Dialog
      open
      fullWidth
      maxWidth="xs"
      aria-labelledby={`${testId}-title`}
      slotProps={{
        paper: {
          'data-testid': testId,
          sx: dialogStyles.paper,
        },
      }}
      role="dialog"
    >
      <DialogTitle id={`${testId}-title`}>{formatMessage('enrollment.cboStep.assignmentUpdateTitle')}</DialogTitle>
      <DialogContent sx={dialogStyles.content}>
        <Stack spacing={2}>
          <Typography>{formatMessage(notAllowedDescription[reason], insuranceLinks)}</Typography>
          <Stack spacing={1} role="group" aria-label={formatMessage('common.options')}>
            <Button
              fullWidth
              size="large"
              variant="contained"
              color="primary"
              loading={patientUpdate.isPending}
              onClick={() => patientUpdate.mutateAsync({ subscribe: true })}
              data-testid={`${testId}-notify-positive`}
            >
              {formatMessage('enrollment.cboStep.subscribe')}
            </Button>
          </Stack>
          <Button
            fullWidth
            size="large"
            color="secondary"
            variant="outlined"
            onClick={() => contactSupport()}
            data-testid={`${testId}-customer-support`}
          >
            {formatMessage('address.nocbo.contact')}
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  )
}

export function WaitlistDialog() {
  const testId = 'waitlist-dialog'
  const formatMessage = useFormatMessage()

  const me = useMe()
  const patientUpdate = usePatientUpdate()

  return (
    <Dialog
      open
      fullWidth
      maxWidth="xs"
      aria-labelledby={`${testId}-title`}
      slotProps={{
        paper: {
          'data-testid': testId,
          sx: dialogStyles.paper,
        },
      }}
      role="dialog"
    >
      <DialogTitle id={`${testId}-title`}>{formatMessage('enrollment.insuranceStep.question.title')}</DialogTitle>
      <DialogContent sx={dialogStyles.content}>
        <Stack spacing={2}>
          <Typography>{formatMessage('enrollment.insuranceStep.waitingList.title', { cboName: me.cbo?.name })}</Typography>
          <Typography>{formatMessage('enrollment.insuranceStep.waitingList.description')}</Typography>
          <Stack spacing={1} role="group" aria-label={formatMessage('common.options')}>
            <Button
              fullWidth
              size="large"
              variant="contained"
              color="primary"
              loading={patientUpdate.isPending}
              onClick={() => patientUpdate.mutateAsync({ waitlist: true })}
              data-testid={`${testId}-notify-positive`}
            >
              {formatMessage('enrollment.insuranceStep.waitingList.yes')}
            </Button>
            <Button
              fullWidth
              size="large"
              variant="outlined"
              color="secondary"
              loading={patientUpdate.isPending}
              onClick={() => patientUpdate.mutateAsync({ waitlist: false })}
              data-testid={`${testId}-notify-negative`}
            >
              {formatMessage('enrollment.insuranceStep.waitingList.no')}
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  )
}
